import { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useMergedStyles } from 'hooks/useMergedStyles';
import { Styles } from 'types/styles';
import SpinnerCircular from '../Icons/SpinnerCircular/SpinnerCircular';
import Check from '../Icons/LandingPageIcons/Check';

import styles from './Button.module.scss';

export enum ButtonType {
  DEFAULT = 'default',
  SECONDARY = 'secondary',
  CANCEL = 'cancel',
  FLAT = 'flat',
  LINK = 'link',
  ICON = 'icon',
  IS_LOADING = 'is-loading',
  WARNING = 'warning',
}

type Props = {
  handleClick?: () => void;
  newStyles?: Styles;
  type?: ButtonType;
  additionalClass?: string;
  disabled?: boolean;
  buttonType?: 'button' | 'submit' | 'reset';
  hasAnimation?: boolean;
  startAnimation?: boolean;
  successAnimationText?: string;
  onAnimationEnd?: () => void;
};
const Button: FC<Props> = ({
  handleClick,
  newStyles = {},
  type = '',
  additionalClass = '',
  disabled = false,
  buttonType = 'button',
  children,
  hasAnimation = false,
  startAnimation = false,
  successAnimationText = '',
  onAnimationEnd,
}) => {
  const mergedStyles = useMergedStyles(styles, newStyles);
  const [showSuccess, setShowSuccess] = useState(false);
  const [buttonWidth, setButtonWidth] = useState<number | ''>('');
  const ref1 = useRef<HTMLSpanElement>(null);
  const ref2 = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (hasAnimation && startAnimation) {
      setShowSuccess(true);
    }
  }, [hasAnimation, startAnimation]);

  useEffect(() => {
    if (hasAnimation) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 2000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [hasAnimation, showSuccess]);

  useEffect(() => {
    if (showSuccess) {
      setTimeout(() => {
        if (onAnimationEnd) onAnimationEnd();
      }, 2000);
    }
  }, [onAnimationEnd, showSuccess]);

  useEffect(() => {
    if (hasAnimation) {
      const buttonPaddingsAndBorders =
        (ref2.current?.getBoundingClientRect().width || 0) -
        (ref2.current?.clientWidth || 0);

      const buttonWidthTemporary =
        60 + buttonPaddingsAndBorders + (ref1.current?.offsetWidth || 0);

      if (buttonWidthTemporary > 0) setButtonWidth(buttonWidthTemporary);
    }
  }, [hasAnimation, showSuccess]);

  return (
    <button
      data-cy={buttonType}
      className={clsx(mergedStyles.button, {
        [mergedStyles[type]]: type,
        [additionalClass]: additionalClass,
        [mergedStyles['has-animation']]: hasAnimation,
        [mergedStyles['show-success']]: showSuccess,
      })}
      onClick={handleClick}
      // eslint-disable-next-line react/button-has-type
      type={buttonType}
      disabled={disabled || showSuccess}
      style={{ width: buttonWidth }}
      ref={ref2}
    >
      {type === ButtonType.IS_LOADING && <SpinnerCircular newStyles={mergedStyles} />}
      <span className={mergedStyles['button-span']} ref={ref1} data-cy="button-text">
        {hasAnimation && showSuccess ? (
          <>
            <Check />
            {successAnimationText}
          </>
        ) : (
          <>{children}</>
        )}
      </span>
    </button>
  );
};

export default Button;
